<template>
  <div class="flex h-screen bg-white font-roboto">
    <!-- Sidebar -->
    <div v-if="isAuthReady && sidebarTabs.length > 0" :class="['transition-all duration-300', isSidebarShrunk ? 'w-16' : 'w-72']"
      class="relative bg-custom-blue text-white">
      <div class="flex items-center justify-between px-6 py-3">        
        <p v-if="!isSidebarShrunk">
          {{ facilityData.name }}
        </p>
        <div>
          <button @click="toggleSidebar" class="text-white focus:outline-none">
            <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
              width="24" height="24" fill="none" viewBox="0 0 24 24">
              <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M5 7h14M5 12h14M5 17h14" />
            </svg>
          </button>
        </div>
      </div>
      <div class="flex items-center justify-center px-6 py-1">
        <div v-if="!isSidebarShrunk" class="flex items-center">
          <img :src="facilityLogo" alt="Facility Logo" class="object-cover w-18 h-10" @error="onImageError" />
        </div>
      </div>

      <nav>
        <!-- Loop through sidebarTabs -->
        <div v-for="tab in sidebarTabs" :key="tab.name">
          <router-link v-if="!tab.children" :to="tab.route"
            :class="[isActive(tab) ? activeClass : '', 'flex items-center px-4 py-3 m-2 duration-200 text-white rounded-md']">
            <div v-html="tab.icon" class="w-6 h-6"></div>
            <!-- Conditionally show the name based on sidebar state -->
            <span v-if="!isSidebarShrunk" class="ml-4">{{ tab.name }}</span>
          </router-link>

          <!-- Dropdown tabs -->
          <div v-else>
            <div class="flex items-center px-4 py-3 m-2 duration-200 text-white rounded-md cursor-pointer"
              @click="toggleDropdown(tab)">
              <div v-html="tab.icon" class="w-6 h-6"></div>
              <span v-if="!isSidebarShrunk" class="ml-4">{{ tab.name }}</span>
            </div>
            <div v-if="isDropdownOpen(tab)" :class="[isSidebarShrunk ? 'ml-0' : 'ml-8']">
              <router-link v-for="child in tab.children" :key="child.name" :to="child.route"
                :class="[isActive(child) ? activeClass : '', 'flex items-center px-4 py-2 m-2 duration-200 text-white rounded-md']">
                <div v-html="child.icon" class="w-5 h-5"></div>
                <span v-if="!isSidebarShrunk" class="ml-4">{{ child.name }}</span>
              </router-link>
            </div>
          </div>
        </div>
      </nav>
    </div>

    <!-- Main content -->
    <div class="flex-1 flex flex-col overflow-hidden" :class="$store.getters['auth/backgroundColor']">
      <Header />
      <main class="flex-1 overflow-x-hidden overflow-y-auto">
        <div class="mx-auto px-6 py-2">
          <slot />
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import getFilteredRoutes from '@/config/sidebar'; // Import the function
import Header from "./HeaderLayout.vue";
import { mapGetters } from 'vuex';
import defaultLogo from '@/assets/images/damusasa_logo.svg';

export default {
  name: 'SidebarLayout',
  components: {
    Header
  },
  data() {
    return {
      sidebarTabs: [], // Initialize as empty array
      activeClass: 'bg-custom-yellow',
      openDropdown: null,
      isSidebarShrunk: false,
      user: null,
      userData: null,
      facilityData: {
        name: '',
        email: '',
        facility_code: '',
        facility_type: '',
        contact_person_name: '',
        contact_person_phone: '',
        country_id: '',
        county_id: '',
        second_level_devision: '',
        third_level_devision: '',
        longitude: '',
        latitude: '',
        account_status: '',
        logo: '',
      },
      isAuthReady: false, // Track auth readiness
    };
  },

  async created() {
    // Fetch user data and wait for auth state to be ready
    await this.initializeUserData();
    if (!this.user) {
      this.$router.push({ name: 'Login' });
      return;
    }
    this.updateSidebarTabs(); // Compute sidebar tabs after auth is ready
    this.isAuthReady = true; // Set flag once auth is ready
  },

  watch: {
    // Watch for changes in the auth state to update sidebar tabs
    user(newUser) {
      if (!newUser) {
        this.$router.push({ name: 'Login' });
      } else {
        this.updateSidebarTabs();
      }
    }
  },

  computed: {
    ...mapGetters({
      token: 'auth/token',
    }),
    facilityLogo() {
      return this.facilityData.logo || defaultLogo;
    },
  },

  beforeRouteEnter(to, from, next) {
    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      next({ name: 'Login' });
    } else {
      next();
    }
  },

  methods: {
    async initializeUserData() {
      // Fetch user data from Vuex store
      this.user = this.$store.getters["auth/user"];
      if (!this.user) {
        // Dispatch an action to fetch user data if not present
        try {
          await this.$store.dispatch('auth/fetchUser'); // Ensure this action exists in your Vuex store
          this.user = this.$store.getters["auth/user"];
        } catch (error) {
          console.error('Failed to fetch user data:', error);
          // Redirect to login if fetching user fails
          this.$router.push({ name: 'Login' });
          return;
        }
      }

      if (!this.user) {
        this.$router.push({ name: 'Login' });
        return;
      }

      if (this.user && this.user.user_roles?.length > 0) {
        const facility = this.user.user_roles[0]?.facility_department?.facility;
        if (facility) {
          this.facilityData = { ...facility };
          this.facilityData.logo = facility.logo || defaultLogo;
        }
      }
    },

    updateSidebarTabs() {
      // Compute sidebar tabs using the function from config/sidebar
      this.sidebarTabs = getFilteredRoutes();
    },

    toggleSidebar() {
      this.isSidebarShrunk = !this.isSidebarShrunk;
    },

    isActive(tab) {
      return this.$route.path === tab.route;
    },

    toggleDropdown(tab) {
      this.openDropdown = this.openDropdown === tab ? null : tab;
    },

    isDropdownOpen(tab) {
      return this.openDropdown === tab;
    },

    onImageError(event) {
      event.target.src = defaultLogo;
    },
  },
};

// Helper function to check if the token is expired
function isTokenExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp && payload.exp < currentTime;
  } catch (error) {
    console.error('Failed to decode token:', error);
    return true;
  }
}
</script>